import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheckered, X, ArrowSquareOut } from "@phosphor-icons/react";
import { Chapter, Reveal } from "./Reveal";

export default function Guarantee() {
  const [open, setOpen] = useState(false);

  return (
    <section id="security" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32" data-testid="guarantee-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] border border-brand-500/25 bg-gradient-to-br from-pine via-night to-pine px-6 py-14 sm:px-12 lg:px-20 lg:py-20">
          <div className="pointer-events-none absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-brand-500/15 blur-[110px]" aria-hidden="true" />

          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4">
                <span className="font-display text-sm font-semibold text-brand-400">02</span>
                <span className="h-px w-12 bg-brand-500/50" />
                <span className="text-xs font-medium uppercase tracking-[0.3em] text-mist">Our Commitment</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-semibold tracking-tighter text-paper sm:text-5xl" data-testid="guarantee-heading">
                Protection backed by{" "}
                <span className="bg-gradient-to-r from-brand-300 to-brand-500 bg-clip-text text-transparent">clear terms.</span>
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
                If an eligible transaction is accepted under Rupora's applicable guarantee terms
                and a covered issue occurs, Rupora will provide compensation according to those
                terms — potentially up to{" "}
                <strong className="text-paper">2× the applicable transaction amount</strong>.
              </p>
              <p className="mt-5 max-w-2xl text-xs leading-relaxed text-mist/70" data-testid="guarantee-disclaimer">
                Guarantee coverage is subject to transaction eligibility, verification, applicable
                terms and conditions, and approved transaction records. It does not apply to
                fraudulent, unauthorized, disputed, or otherwise excluded transactions.
              </p>
              <button
                onClick={() => setOpen(true)}
                data-testid="guarantee-terms-button"
                className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-brand-500/40 px-6 py-3 text-sm font-semibold text-brand-300 transition-all duration-300 hover:bg-brand-500/10"
              >
                View Guarantee Terms <ArrowSquareOut size={15} weight="bold" />
              </button>
            </div>

            <div className="hidden justify-center lg:col-span-4 lg:flex">
              <div className="flex h-44 w-44 items-center justify-center rounded-[2.5rem] border border-brand-500/25 bg-brand-500/10">
                <ShieldCheckered size={88} weight="duotone" className="text-brand-400" />
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-night/70 p-4 backdrop-blur-sm sm:items-center"
            onClick={() => setOpen(false)}
            data-testid="guarantee-terms-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-line bg-pine p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <h3 className="font-display text-xl font-semibold tracking-tight text-paper">Guarantee Terms</h3>
                <button onClick={() => setOpen(false)} aria-label="Close" data-testid="guarantee-terms-close" className="rounded-full p-1.5 text-mist transition-colors hover:bg-night hover:text-paper">
                  <X size={18} />
                </button>
              </div>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-mist">
                <p><strong className="text-paper">Eligibility.</strong> Coverage applies only to transactions accepted under Rupora's applicable guarantee terms and completed through the official Rupora platform.</p>
                <p><strong className="text-paper">Verification.</strong> Every transaction must pass the required verification and compliance checks before it can be considered eligible.</p>
                <p><strong className="text-paper">Coverage.</strong> If a covered issue occurs on an eligible transaction, compensation is provided according to the applicable terms — potentially up to 2× the applicable transaction amount.</p>
                <p><strong className="text-paper">Exclusions.</strong> The guarantee does not apply to fraudulent, unauthorized, disputed, or otherwise excluded transactions, nor to transactions lacking approved records.</p>
                <p><strong className="text-paper">Claims.</strong> Coverage decisions follow approved transaction records and the applicable terms and conditions in force at the time of the transaction.</p>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
